import {
  Target, Eye, Award, HeartPulse, ShieldCheck, Lightbulb, HeartHandshake, Users,
  Check, Recycle, Flame, Sprout, Apple, Briefcase, Package,
} from "lucide-react";
import {
  IMAGES, AUDIENCES, POSITIONING, CORE_VALUES, DIFFERENTIATORS, SUSTAINABILITY, SOCIAL_IMPACT, SDGS, SEO_PAGES,
} from "@/data/site";
import { PageHero, Eyebrow, SectionHeading, ConversionRow } from "@/components/Shared";
import { Seo } from "@/components/Seo";

const ICONS = {
  award: Award, "heart-pulse": HeartPulse, "shield-check": ShieldCheck, lightbulb: Lightbulb,
  "heart-handshake": HeartHandshake, users: Users, recycle: Recycle, flame: Flame,
  sprout: Sprout, apple: Apple, briefcase: Briefcase, package: Package,
};

const About = () => {
  return (
    <div data-testid="about-page">
      <Seo title={SEO_PAGES.about.title} description={SEO_PAGES.about.description} />
      <PageHero
        eyebrow="About Us"
        title="The story of Faf's & Rivella Enterprise"
        intro="Healthy habits start now. We craft natural, locally sourced food and drinks that promote wellness and great taste — while building community across Ghana."
        image={IMAGES.aboutFounder}
        imageAlt="Fresh natural produce"
      />

      {/* Brand structure */}
      <section className="section-py bg-brand-alt">
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Eyebrow>Our Brand Structure</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-brand-ink">
              One company. One promise. Many ways to enjoy it.
            </h2>
            <p className="mt-5 text-brand-slate leading-relaxed text-base md:text-lg">
              <strong className="text-brand-ink">Rivella Enterprise</strong> is the parent company —
              the engine of sourcing, production and quality. <strong className="text-brand-ink">Faf's </strong>
              is our flagship consumer brand: the friendly face you see on every
              bottle, pastry and event bar. Together, they deliver natural
              goodness you can trust.
            </p>
          </div>
          <div className="grid gap-5">
            <div className="rounded-3xl bg-white border border-black/5 p-8">
              <p className="eyebrow text-brand-green">The Company</p>
              <h3 className="mt-2 text-2xl font-semibold text-brand-ink">Rivella Enterprise</h3>
              <p className="mt-3 text-brand-slate">Sourcing, production, quality assurance and distribution across Ghana.</p>
            </div>
            <div className="rounded-3xl bg-brand-greenDark text-white p-8">
              <p className="eyebrow text-white/60">The Brand</p>
              <h3 className="mt-2 text-2xl font-semibold">Faf's</h3>
              <p className="mt-3 text-white/75">Premium juices, smoothies, traditional drinks, pastries, spices & event bars.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <p className="text-center text-brand-purple font-medium tracking-[0.15em] uppercase text-sm">{POSITIONING.tagline}</p>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div data-testid="vision-card" className="rounded-3xl border border-black/5 p-10">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-lilac text-brand-purpleDark"><Eye size={26} /></div>
              <h3 className="mt-6 text-2xl font-medium text-brand-ink">Our Vision</h3>
              <p className="mt-3 text-brand-slate leading-relaxed text-base md:text-lg">{POSITIONING.vision}</p>
            </div>
            <div data-testid="mission-card" className="rounded-3xl border border-black/5 p-10">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-mist text-brand-greenDark"><Target size={26} /></div>
              <h3 className="mt-6 text-2xl font-medium text-brand-ink">Our Mission</h3>
              <p className="mt-3 text-brand-slate leading-relaxed text-base md:text-lg">{POSITIONING.mission}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="core-values" className="section-py bg-brand-alt">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading align="center" eyebrow="Core Values" title="What we stand for" />
          <div className="mt-14 grid gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {CORE_VALUES.map((v) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="rounded-3xl bg-white border border-black/5 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand-greenDark"><Icon size={22} /></div>
                  <h3 className="mt-4 text-base font-medium text-brand-ink">{v.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section data-testid="differentiators" className="section-py">
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Eyebrow>What Makes Us Different</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-brand-ink">
              Crafted differently, on purpose
            </h2>
            <p className="mt-5 text-brand-slate leading-relaxed text-base md:text-lg">
              From custom orders to naturally sweetened recipes, every choice we
              make is about freshness, honesty and your well-being.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {DIFFERENTIATORS.map((d) => (
              <li key={d} className="flex items-start gap-3 rounded-2xl bg-brand-alt border border-black/5 p-5">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-brand-green text-white shrink-0 mt-0.5"><Check size={13} /></span>
                <span className="text-brand-ink text-sm leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sustainability */}
      <section data-testid="sustainability" className="section-py bg-brand-greenDark text-white">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading light eyebrow="Sustainability" title="Greener with every batch" intro="We are actively reducing our environmental impact across how we serve and how we produce." />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {SUSTAINABILITY.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={s.title} className="rounded-3xl bg-white/5 border border-white/10 p-8">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/10 text-white"><Icon size={26} /></div>
                  <h3 className="mt-5 text-xl font-medium">{s.title}</h3>
                  <p className="mt-3 text-white/75 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Impact */}
      <section data-testid="social-impact" className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading eyebrow="Social Impact" title="Growing healthier communities" intro="Our work supports local farmers and value chains, encourages healthier food choices, creates jobs and offers locally produced alternatives to imported snacks and sugary drinks." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SOCIAL_IMPACT.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={s.title} className="rounded-3xl border border-black/5 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-lilac text-brand-purpleDark"><Icon size={22} /></div>
                  <h3 className="mt-5 text-lg font-medium text-brand-ink">{s.title}</h3>
                  <p className="mt-2 text-sm text-brand-slate leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SDG Alignment */}
      <section data-testid="sdg-alignment" className="section-py bg-brand-alt">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading align="center" eyebrow="Aligned with the UN SDGs" title="Committed to global goals" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SDGS.map((g) => (
              <div key={g.number} data-testid={`sdg-${g.number}`} className="rounded-3xl bg-white border border-black/5 p-8 flex items-center gap-5">
                <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-brand-green text-white text-2xl font-bold">{g.number}</div>
                <div>
                  <p className="eyebrow text-brand-green">SDG {g.number}</p>
                  <h3 className="mt-1 text-lg font-medium text-brand-ink leading-snug">{g.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve + CTA */}
      <section className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading eyebrow="Who We Serve" title="Made for every table in Ghana" intro="Whether it's a family breakfast, a boardroom, a sanctuary or a celebration — Faf's belongs there." />
          <div className="mt-10 flex flex-wrap gap-3">
            {AUDIENCES.map((a) => (
              <span key={a} className="rounded-full bg-brand-alt border border-black/5 px-5 py-2.5 text-brand-ink">{a}</span>
            ))}
          </div>
          <div className="mt-12"><ConversionRow testidPrefix="about-cta" /></div>
        </div>
      </section>
    </div>
  );
};

export default About;
